import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Button, Popup } from "devextreme-react";
import {
    DataGrid,
    Column,
    Selection,
    Sorting,
    Paging,
    FilterRow,
    LoadPanel as DataGridLoadPanel,
} from "devextreme-react/data-grid";
import axios from "axios";
import data from "../../../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { LoadPanel } from 'devextreme-react/load-panel';
import { MESSAGES } from '../../../../../../common/Messages';

function ApplyItemRateSchedule(props) {
    const accessToken = props.oidc.user.access_token;
    const { itemId, lotId, headerId, detailId } = props.showPopup || {};
    
    const [itemDetail, setItemDetail] = useState(null);
    const [rateSchedules, setRateSchedules] = useState([]);
    const [selectedRateScheduleIds, setSelectedRateScheduleIds] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const rateScheduleGridRef = React.useRef(null);

    useEffect(() => {
        if (props.showPopup && props.showPopup.visible && headerId && detailId && itemId) {
            loadData();
        }
    }, [props.showPopup?.visible, headerId, detailId, itemId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load item detail
            const detailResponse = await axios.get(
                `${window._env_.REACT_APP_APIS}/${data.TransactionDetailOperations.GetItemRateScheduleDetail}`,
                {
                    params: {
                        headerId: headerId,
                        detailId: detailId
                    },
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            if (detailResponse.data) {
                setItemDetail(detailResponse.data);
            }

            // Load rate schedules
            const schedulesResponse = await axios.get(
                `${window._env_.REACT_APP_APIS}/${data.TransactionDetailOperations.GetRateSchedules}`,
                {
                    params: {
                        itemId: itemId,
                        lotId: lotId || null
                    },
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            let schedulesData = [];
            if (schedulesResponse.data) {
                if (Array.isArray(schedulesResponse.data)) {
                    schedulesData = schedulesResponse.data;
                } else if (schedulesResponse.data.data && Array.isArray(schedulesResponse.data.data)) {
                    schedulesData = schedulesResponse.data.data;
                }
            }

            setRateSchedules(schedulesData);
            
            // Set initially checked schedule (single selection). If multiple flagged, take the first.
            const checkedIds = schedulesData
                .filter(rs => rs.checked === true)
                .map(rs => rs.rowId);
            setSelectedRateScheduleIds(checkedIds.length ? [checkedIds[0]] : []);
        } catch (error) {
            console.error("Error loading data:", error);
            const errorMsg = error.response?.data?.message || error.message;
            notify({
                message: MESSAGES.ITEM_RATE_SCHEDULE_DETAIL_LOAD_ERROR.replace("{0}", errorMsg),
                width: 400,
            }, 'error', 3000);
        } finally {
            setLoading(false);
        }
    };

    const onRateScheduleSelectionChanged = async (e) => {
        const selectedRowKeys = e.selectedRowKeys || [];
        const first = selectedRowKeys.length ? selectedRowKeys[0] : null;
        setSelectedRateScheduleIds(first ? [first] : []);

        // Update item rate schedule on selection change
        if (first && itemId) {
            setLoading(true);
            try {
                const response = await axios.post(
                    `${window._env_.REACT_APP_APIS}/${data.TransactionDetailOperations.UpdateItemRateSchedule}`,
                    null,
                    {
                        params: {
                            itemId: itemId,
                            rateScheduleId: first
                        },
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    }
                );
                if (!(response.data && response.data.success)) {
                    const errorMsg = response.data?.message || MESSAGES.ITEM_RATE_SCHEDULE_UPDATE_ERROR.replace("{0}", "");
                    notify({
                        message: errorMsg,
                        width: 350,
                    }, 'error', 3000);
                }
            } catch (error) {
                const errorMsg = error.response?.data?.message || error.message;
                notify({
                    message: MESSAGES.ITEM_RATE_SCHEDULE_UPDATE_ERROR.replace("{0}", errorMsg),
                    width: 400,
                }, 'error', 3000);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleApplyCharges = async () => {
        if (selectedRateScheduleIds.length === 0) {
            notify({
                message: MESSAGES.SELECT_RATE_SCHEDULE,
                width: 300,
            }, 'warning', 3000);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${data.TransactionDetailOperations.ApplyItemCharges}`,
                {
                    detailId: detailId,
                    itemId: itemId,
                    lotId: lotId,
                    headerId: headerId,
                    selectedRateScheduleIds: selectedRateScheduleIds
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            if (response.data && response.data.success) {
                notify({
                    message: response.data.message || MESSAGES.ITEM_CHARGES_APPLIED_SUCCESSFULLY,
                    width: 300,
                }, 'success', 3000);
                props.setShowPopup(prevFormData => ({ ...prevFormData, visible: false }));
            } else {
                const errorMsg = response.data?.message || MESSAGES.ITEM_CHARGES_APPLY_ERROR.replace("{0}", "");
                notify({
                    message: errorMsg,
                    width: 400,
                }, 'error', 3000);
            }
        } catch (error) {
            console.error("Error applying charges:", error);
            const errorMsg = error.response?.data?.message || error.message;
            notify({
                message: MESSAGES.ITEM_CHARGES_APPLY_ERROR.replace("{0}", errorMsg),
                width: 400,
            }, 'error', 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        props.setShowPopup(prevFormData => ({ ...prevFormData, visible: false }));
    };

    return (
        <>
            {props.showPopup && props.showPopup.visible === true ? (
                <Popup
                    visible={props.showPopup.visible}
                    onHiding={handleCancel}
                    dragEnabled={true}
                    closeOnOutsideClick={false}
                    showCloseButton={true}
                    title="Select Item Rate Schedule (WMD0054)"
                    width="90vw"
                    height="90vh"
                >
                    <LoadPanel
                        visible={loading}
                        showIndicator={true}
                        shading={true}
                        showPane={true}
                    />
                    <div style={{ padding: '10px', height: 'calc(90vh - 100px)', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>
                                Please select the Item Rate Schedule for this Item from the list below
                            </p>
                        </div>

                        {/* Top Grid - Item Details */}
                        <div style={{ marginBottom: '20px', flex: '0 0 auto' }}>
                            <h4 style={{ marginBottom: '10px' }}>Item Details</h4>
                            <DataGrid
                                dataSource={itemDetail ? [itemDetail] : []}
                                showBorders={true}
                                height={150}
                            >
                                <Column dataField="itemCode" caption="Item" width={150} />
                                <Column dataField="itemDescription1" caption="Item Description - 1" width={200} />
                                <Column dataField="lotCode" caption="Lot" width={150} />
                                <Column dataField="lotDescription1" caption="Lot Description - 1" width={200} />
                                <Column dataField="classCode" caption="Class" width={100} />
                                <Column dataField="subClassCode" caption="Sub Class" width={100} />
                            </DataGrid>
                        </div>

                        {/* Bottom Grid - Rate Schedules */}
                        <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <h4 style={{ marginBottom: '10px' }}>Rate Schedules</h4>
                            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                                <DataGrid
                                    ref={rateScheduleGridRef}
                                    dataSource={rateSchedules}
                                    keyExpr="rowId"
                                    showBorders={true}
                                    selectedRowKeys={selectedRateScheduleIds}
                                    onSelectionChanged={onRateScheduleSelectionChanged}
                                    height="100%"
                                >
                                    <Selection mode="single" showCheckBoxesMode="always" />
                                    <Sorting mode="single" />
                                    <FilterRow visible={true} />
                                    <Column dataField="scheduleCode" caption="Rate Schedule" width={150} />
                                    <Column dataField="description" caption="Description" />
                                </DataGrid>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <Button
                                text="Apply Item Charges"
                                type="default"
                                onClick={handleApplyCharges}
                                disabled={loading || selectedRateScheduleIds.length === 0}
                            />
                            <Button
                                text="Cancel"
                                type="default"
                                onClick={handleCancel}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </Popup>
            ) : (null)}
        </>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ApplyItemRateSchedule);
